import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useRecoilState } from "recoil";

import { useUser } from "MVVM/Hooks/useUser";
import { Cs2ClipConfigSaved } from "MVVM/State";
import { USER_UPDATE_CS2_CLIP_CONFIG } from "MVVM/GraphQL/user";
import { analytics } from "MVVM/Analytics";
import { ICs2Crosshair, ICs2Hud } from "MVVM/@types/user";

const CS2_HUD_DEFAULTS: ICs2Hud = {
  showHud: false,
  color: 0,
  topPanel: "COLLAPSED",
  radar: false,
  money: false,
  equipment: false,
  chat: false,
  killfeed: true,
  xray: false,
  onlyMyKills: false,
  pinnedKillfeed: false,
};

const CS2_CROSSHAIR_DEFAULTS: ICs2Crosshair = { mode: "PLAYER" };

// Index is the cl_hud_color value, 0-10. Swatches are approximate.
export const CS2_HUD_COLORS = [
  {
    name: "Team default",
    swatch: "linear-gradient(135deg, #8cb2e6 50%, #e6a03c 50%)",
  },
  { name: "White", swatch: "#ffffff" },
  { name: "Light Blue", swatch: "#8cb2e6" },
  { name: "Dark Blue", swatch: "#4c7fe6" },
  { name: "Purple", swatch: "#b266e6" },
  { name: "Red", swatch: "#e64c4c" },
  { name: "Orange", swatch: "#e68c33" },
  { name: "Yellow", swatch: "#e6d633" },
  { name: "Green", swatch: "#66cc66" },
  { name: "Aquamarine", swatch: "#4cd9d9" },
  { name: "Pink", swatch: "#e680c0" },
];

const normalizeHud = (hud?: Partial<ICs2Hud> | null): ICs2Hud => ({
  showHud: hud?.showHud ?? CS2_HUD_DEFAULTS.showHud,
  color: hud?.color ?? CS2_HUD_DEFAULTS.color,
  topPanel: hud?.topPanel ?? CS2_HUD_DEFAULTS.topPanel,
  radar: hud?.radar ?? CS2_HUD_DEFAULTS.radar,
  money: hud?.money ?? CS2_HUD_DEFAULTS.money,
  equipment: hud?.equipment ?? CS2_HUD_DEFAULTS.equipment,
  chat: hud?.chat ?? CS2_HUD_DEFAULTS.chat,
  killfeed: hud?.killfeed ?? CS2_HUD_DEFAULTS.killfeed,
  xray: hud?.xray ?? CS2_HUD_DEFAULTS.xray,
  onlyMyKills: hud?.onlyMyKills ?? CS2_HUD_DEFAULTS.onlyMyKills,
  pinnedKillfeed: hud?.pinnedKillfeed ?? CS2_HUD_DEFAULTS.pinnedKillfeed,
  // Optional with no default. Normalised to null rather than left undefined so
  // the draft keeps a stable shape for the dirty comparison, and so clearing a
  // slider sends an explicit null.
  radarSize: hud?.radarSize ?? null,
  mapZoom: hud?.mapZoom ?? null,
  hudScale: hud?.hudScale ?? null,
});

const normalizeCrosshair = (
  crosshair?: Partial<ICs2Crosshair> | null,
): ICs2Crosshair => ({
  mode: crosshair?.mode ?? CS2_CROSSHAIR_DEFAULTS.mode,
  ...(crosshair?.code ? { code: crosshair.code } : {}),
});

type TSaveStatus = "idle" | "saved" | "failed";

export const useCs2Hud = () => {
  const { allstarUser } = useUser();
  const isLoggedIn = Boolean(allstarUser?.loggedIn);
  const profileConfig = allstarUser?.user?.cs2ClipConfig;

  // Saves land here rather than in the AllstarUser atom. Rewriting global user
  // state remounts its consumers, which closes whichever modal opened this one.
  const [savedConfig, setSavedConfig] = useRecoilState(Cs2ClipConfigSaved);

  const config = savedConfig ?? profileConfig;

  const server = normalizeHud(config?.hud);
  const serverCrosshair = normalizeCrosshair(config?.crosshair);

  const [draft, setDraft] = useState<ICs2Hud>(server);
  const [crosshair, setCrosshair] = useState<ICs2Crosshair>(serverCrosshair);
  const [saveStatus, setSaveStatus] = useState<TSaveStatus>("idle");

  // Reset the draft when the saved value changes. Keyed on the serialised value
  // rather than the object, which is rebuilt on every render.
  const serverKey = JSON.stringify(server);
  const serverCrosshairKey = JSON.stringify(serverCrosshair);

  useEffect(() => {
    setDraft(JSON.parse(serverKey));
  }, [serverKey]);

  useEffect(() => {
    setCrosshair(JSON.parse(serverCrosshairKey));
  }, [serverCrosshairKey]);

  useEffect(() => {
    if (saveStatus === "idle") return undefined;

    const timer = setTimeout(() => setSaveStatus("idle"), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const [updateHud, { loading: saving }] = useMutation(
    USER_UPDATE_CS2_CLIP_CONFIG,
    {
      onCompleted: (data) => {
        const errors = data?.userUpdateCs2ClipConfig?.errors;
        if (errors?.length) {
          setDraft(server);
          setCrosshair(serverCrosshair);
          setSaveStatus("failed");
          analytics.track("CS2 HUD - Save Error", {
            error: errors[0].message,
          });
          return;
        }

        const saved = data?.userUpdateCs2ClipConfig?.viewer?.cs2ClipConfig;
        const savedHud = normalizeHud(saved?.hud);
        setSavedConfig({
          hud: savedHud,
          crosshair: normalizeCrosshair(saved?.crosshair),
        });
        setSaveStatus("saved");
        analytics.track("CS2 HUD - Saved", { ...savedHud });
      },
      onError: (error) => {
        setDraft(server);
        setCrosshair(serverCrosshair);
        setSaveStatus("failed");
        analytics.track("CS2 HUD - Save Error", { error: error.message });
      },
    },
  );

  const isDirty =
    JSON.stringify(draft) !== serverKey ||
    JSON.stringify(crosshair) !== serverCrosshairKey;

  const save = () => {
    if (saving || !isDirty) return;
    updateHud({ variables: { input: { hud: draft, crosshair } } });
  };

  return {
    draft,
    setDraft,
    crosshair,
    setCrosshair,
    isDirty,
    saving,
    saveStatus,
    save,
    isLoggedIn,
    isConfigured: Boolean(config?.hud),
  };
};
